import { useEffect, useState } from 'react';
import { Download, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

export function CampaignsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    api.get('/admin/campaigns/segments').then(r => setData(r.data)).catch(() => toast.error('Erro ao carregar segmentos'));
  }, []);

  const exportCsv = async (seg, path) => {
    setBusy(seg.key);
    try {
      const res = await api.get(path || `/admin/campaigns/export.csv?segment=${seg.key}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `intimate_${seg.key}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${seg.eligible} contacto(s) exportado(s).`);
    } catch {
      toast.error('Erro ao exportar');
    } finally {
      setBusy('');
    }
  };

  if (!data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-ruby" /></div>;

  return (
    <div className="space-y-6" data-testid="campaigns-panel">
      <Card className="bg-surface border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" />Públicos para campanhas (Brevo)</CardTitle>
          <CardDescription>
            Só são exportadas contas com consentimento de marketing ativo (texto {data.text_version}). O ficheiro CSV tem apenas EMAIL e FIRSTNAME, pronto a importar em Brevo → Contactos → Importar. Não há envio a partir daqui.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.segments.map(s => (
            <div key={s.key} className="p-4 rounded-lg bg-white/5 flex items-center justify-between gap-3" data-testid={`segment-${s.key}`}>
              <div className="min-w-0">
                <p className="text-sm text-foreground font-medium">{s.label}</p>
                <p className="text-xs text-muted-foreground mt-1" data-testid={`segment-${s.key}-counts`}>
                  {s.eligible} com consentimento · {s.total} no total
                </p>
              </div>
              <Button size="sm" variant="outline" className="border-border shrink-0" disabled={!s.eligible || busy === s.key} onClick={() => exportCsv(s)} data-testid={`export-${s.key}`}>
                {busy === s.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}CSV
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="bg-surface border-border" data-testid="withdrawals-card">
        <CardHeader>
          <CardTitle className="text-foreground text-base">Retiradas de consentimento</CardTitle>
          <CardDescription>
            {data.withdrawn} conta(s) retiraram a autorização. No Brevo: Contactos → Importar → este ficheiro → ativar «Colocar na lista de bloqueio os contactos de e-mail importados» (atualizar contactos existentes). Uma importação normal não bloqueia.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button size="sm" variant="outline" className="border-border" disabled={!data.withdrawn || busy === 'withdrawals'} onClick={() => exportCsv({ key: 'withdrawals', eligible: data.withdrawn }, '/admin/campaigns/withdrawals.csv')} data-testid="export-withdrawals">
            {busy === 'withdrawals' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}CSV de retiradas
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
